import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import MuiCard from '@mui/material/Card';
import { styled } from '@mui/material/styles';
import AppTheme from '../shared-theme/AppTheme';
import axios from 'axios';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { getExpiryDate } from '../withAuth';
import ApiClient from '../utils/apiClient';
import Copyright from '../dashboard/internals/components/Copyright';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const Card = styled(MuiCard)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'center',
  width: '100%',
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  margin: 'auto',
  backgroundColor: theme.palette.surface,
  [theme.breakpoints.up('sm')]: {
    maxWidth: '450px',
  },
  [theme.breakpoints.down('sm')]: {    
    padding: theme.spacing(2), // Reduce padding on xs/sm screens
    borderStyle: 'none',
  },
  boxShadow:
    'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
  ...theme.applyStyles('dark', {
    boxShadow:
      'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
  }),
}));

const SignInContainer = styled(Stack)(({ theme }) => ({
  padding: 20,
  [theme.breakpoints.down('sm')]: {
    padding: '0',
    backgroundColor: 'transparent',
  },
  minHeight: '100vh',
  '&::before': {
    content: '""',
    display: 'block',
    position: 'absolute',
    zIndex: -1,
    inset: 0,
    backgroundImage: theme.palette.mode === 'light' 
      ? 'url(/light-bg.jpg)' 
      : 'url(/dark-bg.jpg)',
    backgroundSize: 'cover',
    backgroundPosition: 'left',
    backgroundRepeat: 'no-repeat',
    backgroundAttachment: 'fixed'
  },
}));

export default function ResetPassword() {
    const [emailError, setEmailError] = React.useState(false);
    const [emailErrorMessage, setEmailErrorMessage] = React.useState('');
    const [passwordError, setPasswordError] = React.useState(false);
    const [passwordErrorMessage, setPasswordErrorMessage] = React.useState('');
    const [confirmPasswordError, setConfirmPasswordError] = React.useState(false);
    const [confirmPasswordErrorMessage, setConfirmPasswordErrorMessage] = React.useState('');
    const [isLoading, setIsLoading] = React.useState(false);
    const [emailSent, setEmailSent] = React.useState(false);
    const [isValidToken, setIsValidToken] = React.useState(false);
    const [isExpiredToken, setIsExpiredToken] = React.useState(false);
    const [submitError, setSubmitError] = React.useState('');
  const [userEmail, setUserEmail] = React.useState('');
  const [emailValue, setEmailValue] = React.useState('');
  const [newPasswordValue, setNewPasswordValue] = React.useState('');
  const [confirmPasswordValue, setConfirmPasswordValue] = React.useState('');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const resetToken = searchParams.get('token');

  const checkResetToken = React.useCallback(async () => {
    try {
      const response = await axios.post(`${ApiClient.getCurrentBaseUrl()}/api/users/checkResetToken`, { token: resetToken });
      if (response.status === 200) {
        setIsValidToken(true);
        setUserEmail(response.data.email);
        setEmailValue(response.data.email);
      }
    } catch (error: any) {
      if (error.response?.status === 403) {
        setIsExpiredToken(true);
      } else {
        console.error('Token check failed:', error);
        setIsExpiredToken(true); // Default to expired on other errors
      }
    }
  }, [resetToken]);

  // Check token validity when component loads
  React.useEffect(() => {
    if (resetToken) {
      checkResetToken();
    }
  }, [resetToken, checkResetToken]);

  // Auto-navigate to login for successful password resets
  React.useEffect(() => {
    if (resetToken && isValidToken && emailSent) {
      const timeout = setTimeout(() => {
        navigate('/login');
      }, 2000);
      
      return () => clearTimeout(timeout);
    }
  }, [resetToken, isValidToken, emailSent, navigate]);

    const handleKeyPress = (event: React.KeyboardEvent<HTMLFormElement>) => { 
      if (event.key === 'Enter' && event.target instanceof HTMLInputElement) { 
        event.preventDefault(); 
        handleSubmit(event); 
      } 
    };
  
    const validateEmail = () => {
      const email = emailValue;
      if (!email) {
        setEmailErrorMessage('Email is required.');
        setEmailError(true);
        return false;
      }
      const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailPattern.test(email)) {
        setEmailErrorMessage('Please enter a valid email address.');
        setEmailError(true);
        return false;
      }
      setEmailError(false);
      setEmailErrorMessage('');
      return true;
    };

    const validatePasswords = () => {
      const newPassword = newPasswordValue;
      const confirmPassword = confirmPasswordValue;
      
      let isValid = true;
      
      // Validate new password
      if (!newPassword) {
        setPasswordErrorMessage('New password is required.');
        setPasswordError(true);
        isValid = false;
      } else if (newPassword.length < 6) {
        setPasswordErrorMessage('Password must be at least 6 characters long.');
        setPasswordError(true);
        isValid = false;
      } else {
        setPasswordError(false);
        setPasswordErrorMessage('');
      }
      
      // Validate confirm password
      if (!confirmPassword) {
        setConfirmPasswordErrorMessage('Please confirm your password.');
        setConfirmPasswordError(true);
        isValid = false;
      } else if (newPassword !== confirmPassword) {
        setConfirmPasswordErrorMessage('Passwords do not match.');
        setConfirmPasswordError(true);
        isValid = false;
      } else {
        setConfirmPasswordError(false);
        setConfirmPasswordErrorMessage('');
      }
      
      return isValid;
    };
  
    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      
      // Clear any previous error messages
      setSubmitError('');
      
      if (resetToken && isValidToken) {
        // Password reset mode with token
        if (validatePasswords()) {
          setIsLoading(true);
          try {
            const response = await axios.post(`${ApiClient.getCurrentBaseUrl()}/api/users/resetPassword`, {
              token: resetToken,
              new_password: newPasswordValue,
              confirm_password: confirmPasswordValue
            });
            
            if (response.status === 200) {
              setEmailSent(true);
            }
          } catch (error: any) {
            console.log('Password reset error:', error.response);
            if (error.response) {
              const status = error.response.status;
              if (status === 400) {
                const errorMsg = error.response.data?.msg || 'Invalid password or passwords do not match.';
                setSubmitError(errorMsg);
              } else if (status >= 500) {
                setSubmitError('Sorry, something went wrong at our end. Please try again later.');
              }
            } else {
              setSubmitError('Network error. Please check your connection and try again.');
            }
          } finally {
            setIsLoading(false);
          }
        }
      } else if (!resetToken || !isValidToken) {
        // Email reset mode without token
        if (validateEmail()) {
          setIsLoading(true);
          try {
            const response = await axios.post(`${ApiClient.getCurrentBaseUrl()}/api/users/resetLink`, {
              email: emailValue,
              url: window.location.origin + '/reset'
            });
            
            if (response.status === 200) {
              setEmailSent(true);
            }
          } catch (error: any) {
            console.log(error.response);
            if (error.response) {
              const status = error.response.status;
              if (status >= 400 && status < 500) {
                const errorMsg = error.response.data?.msg || 'Something went wrong. Please try again later.';
                setSubmitError(errorMsg);
              } else if (status >= 500) {
                setSubmitError('Sorry, something went wrong at our end. Please try again later.');
              }
            } else {
              setSubmitError('Network error. Please check your connection and try again.');
            }
          } finally {
            setIsLoading(false);
          }
        }
      }
    };
  
    // Check for existing auth token
    const token = localStorage.getItem("token");
    const isAuth = !!token;
  
    if (isAuth) {
      const expiration = getExpiryDate(token);
      const fiveMinutes = 1000 * 60 * 5;
      const now = new Date();
      if (expiration.getTime() - now.getTime() > fiveMinutes) {
        return (<Navigate to="/" />);
      } else {
        localStorage.removeItem("token");
      }
    }

  return (
    <AppTheme>
      <CssBaseline enableColorScheme />
      <SignInContainer direction="column" justifyContent="space-between">
        <Box sx={{ flexGrow: 1, mt: '10vh' }}>
          <Card variant="outlined">
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/login')}
            sx={{ alignSelf: 'flex-start' }}
          >
            Back to Sign in
          </Button>
          <Typography
            sx={{ width: '100%', fontSize: 'clamp(2rem, 10vw, 2.15rem)', mt: -1 }}
          >
            {resetToken ? 'Reset password' : 'Send reset link'}
          </Typography>
          
          <Typography 
            sx={{ 
              width: '100%', 
              mb: 2,
              fontSize: 'clamp(1rem, 5vw, 1.1rem)',
              opacity: 0.8
            }}
          >
            {resetToken && isExpiredToken 
              ? "Your reset password link has expired. Please enter your e-mail below to send a new one."
              : resetToken && isValidToken
                ? `Create a new password below for ${userEmail}.`
                : resetToken 
                  ? "Enter your new password below."
                  : "Enter your e-mail address to send a reset password link."
            }
          </Typography>

          <Box
            component="form"
            onSubmit={handleSubmit}
            onKeyDown={handleKeyPress}
            noValidate
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              gap: 2,
            }}
          >
            {/* Email field - hidden when valid token is present */}
            {!resetToken || !isValidToken ? (
              <FormControl>
                <FormLabel htmlFor="email">Email</FormLabel>
                <TextField
                  error={emailError}
                  helperText={emailErrorMessage}
                  id="email"
                  type="email"
                  name="email"
                  placeholder="your@email.com"
                  autoComplete="email"
                  autoFocus
                  required
                  fullWidth
                  variant="outlined"
                  color={emailError ? 'error' : 'primary'}
                  sx={{ ariaLabel: 'email' }}
                  value={emailValue}
                  onChange={(e) => setEmailValue(e.target.value)}
                  slotProps={{
                    input: {
                      readOnly: Boolean(resetToken && isValidToken)
                    }
                  }}
                />
              </FormControl>
            ) : null}

            {/* Password fields for valid token mode */}
            {resetToken && isValidToken && (
              <>
                <FormControl>
                  <FormLabel htmlFor="newPassword">New password</FormLabel>
                  <TextField
                    error={passwordError}
                    helperText={passwordErrorMessage}
                    id="newPassword"
                    type="password"
                    name="newPassword"
                    placeholder="Enter new password"
                    autoComplete="new-password"
                    autoFocus
                    required
                    fullWidth
                    variant="outlined"
                    color={passwordError ? 'error' : 'primary'}
                    sx={{ ariaLabel: 'newPassword' }}
                    value={newPasswordValue}
                    onChange={(e) => setNewPasswordValue(e.target.value)}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel htmlFor="confirmPassword">Confirm new password</FormLabel>
                  <TextField
                    error={confirmPasswordError}
                    helperText={confirmPasswordErrorMessage}
                    id="confirmPassword"
                    type="password"
                    name="confirmPassword"
                    placeholder="Confirm new password"
                    autoComplete="new-password"
                    required
                    fullWidth
                    variant="outlined"
                    color={confirmPasswordError ? 'error' : 'primary'}
                    sx={{ ariaLabel: 'confirmPassword' }}
                    value={confirmPasswordValue}
                    onChange={(e) => setConfirmPasswordValue(e.target.value)}
                  />
                </FormControl>
              </>
            )}

            {submitError && (
              <Typography variant="error" sx={{ textAlign: 'center', mt: 1 }}>
                {submitError}
              </Typography>
            )}

            {!emailSent && (
              <>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={isLoading}
                  sx={{mt: 2}}
                >
                  {isLoading 
                    ? 'Processing...' 
                    : resetToken && isValidToken 
                      ? 'Reset password' 
                      : resetToken && isExpiredToken
                        ? 'Send reset password link'
                        : 'Send reset password link'
                  }
                </Button>
              </>
            )}
            {emailSent && (
              <Typography variant="body1" sx={{ textAlign: 'center', mt: 1 }}>
                {resetToken && isValidToken 
                  ? <>Your password has been. Returning to the <Link href="/login">sign in</Link> page...</>
                  : "If you have an account, a reset password link has been sent to the address above."
                }
              </Typography>
            )}
          </Box>          
          </Card>
        </Box>
        <Copyright sx={{ mt: 2, mb: 2 }} />
      </SignInContainer>
    </AppTheme>
  );
}
